import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignLeft } from '@crisp/crisp-ui/icons/classic/regular';
import { HeadingItem } from '../../types';
import { cn } from '../../lib/cn';
import styles from './TableOfContents.module.css';

interface TableOfContentsProps {
  headings: HeadingItem[];
  pageSlug?: string;
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ headings }) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    if (!headings || headings.length === 0) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (let i = headings.length - 1; i >= 0; i--) {
        const item = headings[i];
        const el = document.getElementById(item.id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= scrollPosition) {
            setActiveId(item.id);
            return;
          }
        }
      }

      if (headings[0]) {
        setActiveId(headings[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const scrollToHeading = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setActiveId(id);
      window.history.pushState(null, '', `#${id}`);
    }
  };

  if (!headings || headings.length === 0) {
    return null;
  }

  // Imported articles often start their outline at h4, so the TOC follows the
  // page's shallowest level rather than assuming h2.
  const minDepth = Math.min(...headings.map((h) => h.depth).filter((d) => d >= 2));
  const tocItems = headings.filter((h) => h.depth === minDepth || h.depth === minDepth + 1);

  if (tocItems.length === 0) {
    return null;
  }

  return (
    <aside className={styles.aside}>
      <div className={styles.sticky}>
        <div className={styles.heading}>
          <FontAwesomeIcon icon={faAlignLeft} className="icon-slot" />
          <span>On this page</span>
        </div>

        <ul className={styles.list}>
          {tocItems.map((item) => (
            <li key={item.id} className={item.depth > minDepth ? styles.nested : undefined}>
              <a
                href={`#${item.id}`}
                onClick={(e) => scrollToHeading(item.id, e)}
                className={cn(styles.link, activeId === item.id && styles.linkActive)}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default TableOfContents;
